import { Button, Tooltip } from '@flixbus/honeycomb-react';
import React, { useId, useMemo, useRef } from 'react';

import { Temporal } from '@js-temporal/polyfill';
import { printDate } from '@ridehub/util-temporal';
import styles from './ui-yield-table-cell.module.scss';
import { useLocalization } from '@ridehub/feat-localization';

export type OrdersPBD = {
    orders: Array<{
        bookingClass: string | null;
        orderDateTime: Temporal.ZonedDateTime;
        price: number;
    }>;
    totalTix: string;
};

export const BUCKET_LEGEND = [
    { key: 'b1_10', label: 'B1-B10' },
    { key: 'b11_20', label: 'B11-B20' },
    { key: 'b21_30', label: 'B21-B30' },
    { key: 'b31_40', label: 'B31-B40' },
    { key: 'b41_50', label: 'B41-B50' },
    { key: 'b51_60', label: 'B51-B60' },
    { key: 'b61_70', label: 'B61-B70' },
    { key: 'b71_80', label: 'B71-B80' },
    { key: 'b81_90', label: 'B81-B90' },
    { key: 'b91_100', label: 'B91-B100' },
] as const;

type BucketKey = (typeof BUCKET_LEGEND)[number]['key'];
type ColorBucket = BucketKey | 'no_class';

function getBucket(value: number): BucketKey {
    if (value <= 10) return 'b1_10';
    if (value <= 20) return 'b11_20';
    if (value <= 30) return 'b21_30';
    if (value <= 40) return 'b31_40';
    if (value <= 50) return 'b41_50';
    if (value <= 60) return 'b51_60';
    if (value <= 70) return 'b61_70';
    if (value <= 80) return 'b71_80';
    if (value <= 90) return 'b81_90';
    return 'b91_100';
}

function parseBookingClassNumber(bookingClass: string | null): number | null {
    if (!bookingClass) return null;
    const numeric = parseInt(bookingClass.replace(/\D/g, ''), 10);
    return Number.isNaN(numeric) ? null : numeric;
}

function getBookingWindow(orders: OrdersPBD['orders']): string | null {
    if (!orders.length) return null;
    const sorted = [...orders].sort((a, b) => Temporal.ZonedDateTime.compare(a.orderDateTime, b.orderDateTime));
    const first = sorted[0].orderDateTime;
    const last = sorted[sorted.length - 1].orderDateTime;

    if (Temporal.ZonedDateTime.compare(first, last) === 0) {
        return printDate(first);
    }
    return `${printDate(first)} to ${printDate(last)}`;
}

function groupOrdersByBookingClass(orders: OrdersPBD['orders'], formatPrice: (value: number) => string) {
    const aggregated = new Map<
        string,
        { count: number; priceTotal: number; bucket: ColorBucket; sortWeight: number; displayLabel: string }
    >();

    orders.forEach((order) => {
        const classNumber = parseBookingClassNumber(order.bookingClass);
        const bucket: ColorBucket = classNumber ? getBucket(classNumber) : 'no_class';
        const key = order.bookingClass ?? 'No booking class';
        const priceValue = order.price ?? 0;
        const existing = aggregated.get(key);
        if (existing) {
            aggregated.set(key, {
                ...existing,
                count: existing.count + 1,
                priceTotal: existing.priceTotal + priceValue,
            });
        } else {
            aggregated.set(key, {
                count: 1,
                priceTotal: priceValue,
                bucket,
                sortWeight: classNumber ?? Number.MAX_SAFE_INTEGER,
                displayLabel: key,
            });
        }
    });

    return Array.from(aggregated.values())
        .sort((a, b) => {
            if (a.sortWeight === b.sortWeight) return a.displayLabel.localeCompare(b.displayLabel);
            return a.sortWeight - b.sortWeight;
        })
        .map((entry) => ({ ...entry, priceLabel: formatPrice(entry.priceTotal / entry.count) }));
}

function YieldTableCellTooltip({ data }: { data: OrdersPBD }) {
    const { printWithLocalUnit } = useLocalization();
    const bookingWindow = useMemo(() => getBookingWindow(data.orders), [data.orders]);
    const ordersByClass = useMemo(
        () => groupOrdersByBookingClass(data.orders, (price) => printWithLocalUnit('currency', price, true, 'country')),
        [data.orders, printWithLocalUnit]
    );

    return (
        <div className={`${styles.tooltip} ${styles.colorScope}`}>
            {bookingWindow && (
                <div className={styles.tooltipSection}>
                    <p className={styles.tooltipRange}>{bookingWindow}</p>
                </div>
            )}
            <div className={styles.tooltipSection}>
                <p className={styles.tooltipLabel}>Tix Sold</p>
                <div className={styles.tooltipList}>
                    {ordersByClass.map((entry) => (
                        <div key={`${entry.displayLabel}-${entry.bucket}`} className={styles.tooltipItem}>
                            <div className={styles.tooltipBadge}>
                                <span
                                    className={`${styles.tooltipSwatch} ${
                                        styles[`legend-${entry.bucket}`] ?? styles['legend-no_class']
                                    }`}
                                ></span>
                                {entry.displayLabel}
                            </div>
                            <div className={styles.tooltipPrice}>
                                {entry.count} ({entry.priceLabel})
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

export function YieldTableCell({ data }: { data: OrdersPBD }) {
    const tooltipId = useId();
    const buttonRef = useRef<HTMLButtonElement>(null);

    if (!data || data.totalTix === '0') {
        return <span className={styles.emptyCell}></span>;
    }

    const bookingClassNumbers = data.orders
        .map((order) => {
            if (!order.bookingClass) return NaN;
            return parseInt(order.bookingClass.replace(/\D/g, ''), 10);
        })
        .filter((num) => !Number.isNaN(num));

    const hasBookingClasses = bookingClassNumbers.length > 0;
    const minClass = hasBookingClasses ? Math.min(...bookingClassNumbers) : null;
    const maxClass = hasBookingClasses ? Math.max(...bookingClassNumbers) : null;

    const bookingBuckets: Array<ColorBucket> = hasBookingClasses
        ? bookingClassNumbers.map((num) => getBucket(num))
        : ['no_class'];
    const uniqueBuckets = new Set(bookingBuckets);

    const minBucket: ColorBucket = hasBookingClasses && minClass !== null ? getBucket(minClass) : 'no_class';
    const maxBucket: ColorBucket = hasBookingClasses && maxClass !== null ? getBucket(maxClass) : 'no_class';
    const hasRange = hasBookingClasses && uniqueBuckets.size > 1;
    const hasAdditionalBuckets = hasBookingClasses && uniqueBuckets.size > 2;

    const classNames = [
        styles.cell,
        styles.colorScope,
        hasBookingClasses ? styles[`min-${minBucket}`] : styles.noClass,
        hasRange ? styles[`max-${maxBucket}`] : '',
    ]
        .filter(Boolean)
        .join(' ');

    return (
        <Tooltip
            id={`yield-table-${tooltipId}`}
            content={<YieldTableCellTooltip data={data} />}
            wrapAround="block"
            targetRef={buttonRef}
            position="bottom"
            openOnHover={true}
        >
            <Button
                innerRef={buttonRef}
                type="button"
                appearance="link"
                extraClasses={classNames}
                aria-label="Show order details"
            >
                <span className={styles.value}>
                    {data.totalTix}
                    {hasAdditionalBuckets && <span className={styles.multipleIndicator}>*</span>}
                </span>
            </Button>
        </Tooltip>
    );
}

export function YieldTableLegend() {
    return (
        <div className={`${styles.legend} ${styles.colorScope}`}>
            <div className={styles.legendItems}>
                {BUCKET_LEGEND.map(({ key, label }) => (
                    <span key={key} className={styles.legendItem}>
                        <span className={`${styles.legendSwatch} ${styles[`legend-${key}`]}`}></span>
                        {label}
                    </span>
                ))}
                <span className={styles.legendItem}>
                    <span className={`${styles.legendSwatch} ${styles['legend-no_class']}`}></span>
                    No booking class
                </span>
            </div>
            <p className={styles.legendNote}>* Multiple Booking clusters are present</p>
        </div>
    );
}

export default YieldTableCell;
